import React, { useState, useEffect } from 'react';
import './QuestionBankEditor.css';

const QuestionBankEditor = ({ onBack }) => {
  const [selectedSubject, setSelectedSubject] = useState('');
  const [bankTitle, setBankTitle] = useState('');
  const [markdownContent, setMarkdownContent] = useState('');
  const [parsedQuestions, setParsedQuestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [saveStatus, setSaveStatus] = useState('');
  const [subjects, setSubjects] = useState({});

  // 学科选项
  const subjectOptions = {
    'pharmacology': '药理学',
    'pathology': '病理学', 
    'diagnostics': '诊断学',
    'mao-zedong-thought': '毛泽东思想和中国特色社会主义理论体系概论'
  };

  // 获取现有学科数据
  useEffect(() => {
    const loadSubjects = async () => {
      try {
        const response = await fetch('/question-banks/index.json');
        const data = await response.json();
        setSubjects(data.subjects || {});
      } catch (error) {
        console.error('加载学科数据失败:', error);
      }
    };
    loadSubjects();
  }, []);

  // 解析Markdown内容为题目数组
  const parseMarkdownToQuestions = (markdown) => {
    const questions = [];
    
    // 按题目分割，每个题目以数字开头
    const questionBlocks = markdown.split(/\n(?=\d+\.\s)/);
    
    questionBlocks.forEach((block, index) => {
      if (!block.trim()) return;
      
      const lines = block.trim().split('\n').filter(line => line.trim());
      if (lines.length < 2) return;
      
      const questionMatch = lines[0].match(/^\d+\.\s*(?:\([^)]+\))?\s*(.+)$/);
      if (!questionMatch) return;
      
      const questionText = questionMatch[1];
      const options = [];
      let answer = '';
      let explanation = '';
      
      let currentSection = 'options';
      
      for (let i = 1; i < lines.length; i++) {
        const line = lines[i].trim();
        
        // 选项（A. B. C. D. E.）
        if (line.match(/^[A-E]\.\s/)) {
          options.push(line);
        }
        // 答案标识
        else if (line.match(/^答案[:：]\s*/)) {
          currentSection = 'answer';
          answer = line.replace(/^答案[:：]\s*/, '').trim();
        }
        // 解析标识
        else if (line.match(/^解析[:：]\s*/)) {
          currentSection = 'explanation';
          explanation = line.replace(/^解析[:：]\s*/, '').trim();
        }
        // 继续添加到当前部分
        else if (currentSection === 'answer' && !answer) {
          answer = line;
        }
        else if (currentSection === 'explanation') {
          explanation += (explanation ? '\n' : '') + line;
        }
      }
      
      if (questionText && options.length >= 2) {
        questions.push({
          question: `${questions.length + 1}. ${questionText}`,
          options,
          answer,
          explanation: explanation || '',
          detailedExplanation: '',
          relatedQuestions: []
        });
      }
    });
    
    return questions;
  };

  // 当Markdown内容改变时，自动解析
  useEffect(() => {
    if (markdownContent.trim()) {
      const questions = parseMarkdownToQuestions(markdownContent);
      setParsedQuestions(questions);
    } else {
      setParsedQuestions([]);
    }
  }, [markdownContent]);

  // 处理文件上传
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file && file.type === 'text/markdown') {
      const reader = new FileReader();
      reader.onload = (e) => {
        setMarkdownContent(e.target.result);
        // 从文件名中提取题库标题
        const filename = file.name.replace('.md', '');
        setBankTitle(filename);
      };
      reader.readAsText(file);
    } else {
      alert('请选择Markdown文件(.md)');
    }
  };

  // 保存题库
  const handleSave = async () => {
    if (!selectedSubject || !bankTitle || parsedQuestions.length === 0) {
      alert('请完善所有必填信息');
      return;
    }

    setIsLoading(true);
    setSaveStatus('正在保存...');

    try {
      // 构建题库数据
      const bankData = {
        title: bankTitle,
        totalCount: parsedQuestions.length,
        questions: parsedQuestions
      };

      // 发送保存请求
      const response = await fetch('/api/save-question-bank', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          subject: selectedSubject,
          bankTitle,
          data: bankData
        })
      });

      if (response.ok) {
        setSaveStatus('保存成功！');
        setTimeout(() => {
          setSaveStatus('');
          // 可选择清空表单或返回列表
          if (window.confirm('题库保存成功！是否清空表单继续添加？')) {
            setBankTitle('');
            setMarkdownContent('');
            setParsedQuestions([]);
          }
        }, 2000);
      } else {
        throw new Error('保存失败');
      }
    } catch (error) {
      console.error('保存题库失败:', error);
      setSaveStatus('保存失败，请重试');
      setTimeout(() => setSaveStatus(''), 3000);
    } finally {
      setIsLoading(false);
    }
  };

  // 示例Markdown格式
  const exampleMarkdown = `1. (单选题)关于细胞水肿的描述，错误的是
A. 细胞体积增大
B. 胞质内出现颗粒
C. 线粒体和内质网肿胀
D. 属于不可逆性损伤
答案：D
解析：细胞水肿是可逆性损伤，不会导致细胞死亡。

2. (单选题)下列哪种剂量会产生副作用
A. 治疗量
B. 最小致死量
C. 中毒量
D. LD50
E. 最小中毒量
答案：A
解析：副作用是指在治疗剂量下出现的与治疗目的无关的作用。`;

  return (
    <div className="question-bank-editor">
      <div className="editor-header">
        <button className="back-button" onClick={onBack}>
          ← 返回
        </button>
        <h2>题库编辑器</h2>
      </div>

      <div className="editor-content">
        <div className="editor-form">
          {/* 学科选择 */}
          <div className="form-group">
            <label>选择学科 *</label>
            <select 
              value={selectedSubject} 
              onChange={(e) => setSelectedSubject(e.target.value)}
              required
            >
              <option value="">请选择学科</option>
              {Object.entries(subjectOptions).map(([key, name]) => (
                <option key={key} value={key}>{name}</option>
              ))}
            </select>
          </div>

          {/* 题库标题 */}
          <div className="form-group">
            <label>题库标题 *</label>
            <input
              type="text"
              value={bankTitle}
              onChange={(e) => setBankTitle(e.target.value)}
              placeholder="例如：绪论、心血管系统等"
              required
            />
          </div>

          {/* 文件上传 */}
          <div className="form-group">
            <label>上传Markdown文件</label>
            <input
              type="file"
              accept=".md"
              onChange={handleFileUpload}
            />
            <small>支持.md格式文件</small>
          </div>

          {/* Markdown编辑区域 */}
          <div className="form-group">
            <label>题库内容 (Markdown格式) *</label>
            <textarea
              value={markdownContent}
              onChange={(e) => setMarkdownContent(e.target.value)}
              placeholder={`请按照以下格式输入题目：\n\n${exampleMarkdown}`}
              rows={15}
              required
            />
          </div>

          {/* 保存按钮 */}
          <div className="form-actions">
            <button 
              className="save-button"
              onClick={handleSave}
              disabled={isLoading || !selectedSubject || !bankTitle || parsedQuestions.length === 0}
            >
              {isLoading ? '保存中...' : '保存题库'}
            </button>
            {saveStatus && (
              <span className={`save-status ${saveStatus.includes('成功') ? 'success' : 'error'}`}>
                {saveStatus}
              </span>
            )}
          </div>
        </div>

        {/* 预览区域 */}
        <div className="preview-area">
          <h3>预览 ({parsedQuestions.length} 道题目)</h3>
          <div className="questions-preview">
            {parsedQuestions.length > 0 ? (
              parsedQuestions.slice(0, 3).map((question, index) => (
                <div key={index} className="question-preview">
                  <div className="question-text">{question.question}</div>
                  <div className="options">
                    {question.options.map((option, optIndex) => (
                      <div key={optIndex} className="option">{option}</div>
                    ))}
                  </div>
                  <div className="answer">
                    <strong>答案:</strong> {question.answer}
                  </div>
                  {question.explanation && (
                    <div className="explanation">
                      <strong>解析:</strong> {question.explanation}
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="no-questions">
                请输入题目内容，将自动解析并显示预览
              </div>
            )}
            {parsedQuestions.length > 3 && (
              <div className="more-questions">
                ... 还有 {parsedQuestions.length - 3} 道题目
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 格式说明 */}
      <div className="format-guide">
        <h4>Markdown格式说明：</h4>
        <ul>
          <li>每道题目以数字开头，如 "1. (单选题)题目内容"</li>
          <li>选项用 A. B. C. D. E. 开头</li>
          <li>答案行以 "答案：" 开头</li>
          <li>解析行以 "解析：" 开头（可选）</li>
          <li>题目之间用空行分隔</li>
        </ul>
      </div>
    </div>
  );
};

export default QuestionBankEditor; 